import React from 'react'
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TableContainer,
  Typography,
  Box
} from '@mui/material'

// Shared column definition for longline-station rows. Exported so the
// map-level "Export Displayed (CSV)" results bar can reuse it without
// redefining the column list — keep this in sync with the rendered table.
export const LONGLINE_STATION_COLUMNS = [
    { label: 'SEAMAP Station', key: 'seamapstation' },
    { label: 'Source', key: 'source' },
    { label: 'Date', key: 'date' },
    { label: 'Depth (m)', key: 'depth' },
    { label: 'Surface Temp (C)', key: 'tempsur' },
    { label: 'Mid Temp (C)', key: 'tempmid' },
    { label: 'Bottom Temp (C)', key: 'tempbot' },
    { label: 'Surface Salinity (ppt)', key: 'salsur' },
    { label: 'Mid Salinity (ppt)', key: 'salmid' },
    { label: 'Bottom Salinity (ppt)', key: 'salbot' },
    { label: 'Surface Dissolved O2 (mg/L)', key: 'dosur' },
    { label: 'Mid Dissolved O2 (mg/L)', key: 'domid' },
    { label: 'Bottom Dissolved O2 (mg/L)', key: 'dobot' }
]

// Normalize a raw row into export shape (collapse `date` → ISO yyyy-mm-dd).
// Shared with the results bar so the CSV from the bar matches the CSV from
// the per-point detail button byte-for-byte.
export const formatLonglineRowForExport = (row) => ({
    ...row,
    date: row && row.date ? new Date(row.date).toISOString().split('T')[0] : '',
})

// `data` is the currently selected station shown in the detail table.
// The per-point CSV button was removed in favor of the always-visible
// ResultsExportBar at the top of the table panel.
export default function PointDataTableLongline({ data }) {
    if (!data) {
        return null
    }

    const onlyDate = data.date 
    ? new Date(data.date).toISOString().split('T')[0] 
    : '';

    const columns = LONGLINE_STATION_COLUMNS

    return (
        <Box sx={{ 
          width: '100%', 
          paddingLeft: { xs: '0.5rem', sm: '1rem' },
          paddingRight: { xs: '0.5rem', sm: '1rem' }
        }}>
            <Box sx={{ marginBottom: { xs: 1, sm: 2 } }}>
                <Typography variant="h6" sx={{ 
                    color: '#2E5A88', 
                    fontSize: { xs: '0.9rem', sm: '1rem' } 
                }}>
                    Bottom Longline Station Details
                </Typography>
            </Box>
            <TableContainer 
                component={Paper} 
                elevation={2}
                sx={{ 
                    width: '100%',
                    overflowX: 'auto', // Enable horizontal scrolling
                    overflowY: 'visible',
                    maxHeight: 'none' // Remove height restrictions
                }}
            >
                <Table sx={{ 
                    width: '100%',
                    minWidth: { xs: '800px', md: '100%' } // Minimum width for mobile scrolling
                }}>
                    <TableHead>
                        <TableRow>
                            {columns.map((col) => (
                                <TableCell
                                    key={col.key}
                                    sx={{
                                        fontWeight: 'bold',
                                        backgroundColor: '#f5f5f5',
                                        borderBottom: '2px solid #ccc',
                                        color: '#1a2a3a',
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        padding: { xs: '8px', sm: '16px' },
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {col.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow sx={{ '&:nth-of-type(odd)': { backgroundColor: 'grey.50' } }}>
                            {columns.map((col) => {
                                let cellValue = col.key === 'date' ? onlyDate : data[col.key];

                                // For SEAMAP Station, ensure it's a string with no decimal
                                if (col.key === 'seamapstation') {
                                    // Convert to string and remove any decimal portion
                                    cellValue = String(cellValue).split('.')[0];
                                }

                                // Check if the value is numeric (or can be converted to a number)
                                else if (!isNaN(cellValue) && cellValue !== null && cellValue !== '') {
                                    // Format as number with one decimal place
                                    cellValue = Number(cellValue).toFixed(1);
                                }

                                return (
                                    <TableCell key={col.key} sx={{ 
                                    color: '#333333',
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    padding: { xs: '8px', sm: '16px' },
                                    whiteSpace: 'nowrap'
                                }}>
                                        {cellValue}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    )
}